import { useEffect } from 'react';
import { API } from '../api/api';
import { isInIframe } from '../utils/frame';

// The popup ends up on the API host, since that is where the OAuth callback
// lives, so that is the only origin whose oauth-success message is trusted.
// Without this check any page framing the calendar could force it to reload.
function apiOrigin() {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    return base ? new URL(base, window.location.href).origin : window.location.origin;
  } catch {
    return window.location.origin;
  }
}

// Opens the Microsoft login popup. Returns false when the browser blocked it,
// so the caller can offer a button instead: window.open loses its user-gesture
// credit across an await, and a popup blocker will often refuse on that basis,
// while the same call inside a click handler is allowed through.
export async function startPopupLogin() {
  const { url } = await API.login();
  const popup = window.open(url, 'oauthPopup', 'width=600,height=700');
  return !!popup;
}

export function useAuthBootstrap({
  authenticated,
  setAuthenticated,
  setProjects,
  setError,
  setBillableAutoFill,
  setCurrentView,
  setDevLoginEnabled,
  setPopupLoginBlocked,
}) {
  // Registered up front rather than when the popup opens: the popup can finish
  // after a re-render, and a listener already in place cannot miss it.
  useEffect(() => {
    const expected = apiOrigin();
    function onMessage(event) {
      if (event.origin !== expected) return;
      if (event.data && event.data.type === 'oauth-success') window.location.reload();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Bootstrap auth + initial data
  useEffect(() => {
    API.checkAuth()
      .then(async (data) => {
        setBillableAutoFill(!!data.billableAutoFill);

        if (!data.authenticated) {
          if (data.devLoginEnabled) {
            // Dev-only bypass: wait for the email form instead of launching Microsoft SSO.
            if (setDevLoginEnabled) setDevLoginEnabled(true);
            return;
          }

          // Embedded: Microsoft refuses to render its login page inside a
          // frame, so a redirect is not available here and the popup is the
          // only route. If the browser blocks it, fall back to a button, which
          // gets a fresh user gesture to spend.
          if (isInIframe()) {
            const opened = await startPopupLogin();
            if (!opened && setPopupLoginBlocked) setPopupLoginBlocked(true);
            return;
          }

          // Standalone: a top-level redirect. This is the case that has to work
          // from an "Add to Home Screen" shortcut, where iOS gives the page no
          // popup to open and no opener to message a result back through.
          const { url } = await API.login();
          window.location.replace(url);
          return;
        }

        setAuthenticated(true);

        // Fetch user's last calendar view preference
        API.getUserViewPreference().then((viewData) => {
          if (viewData.view && setCurrentView) {
            setCurrentView(viewData.view);
          }
        }).catch(() => {});

        // Fetch matters
        API.fetchProjects().then((projectData) => {
          if (projectData.success) setProjects(projectData.data);
        }).catch(() => {});

      })
      .catch(() => {
        setError('Error - contact IT');
      });
  }, []);

  // 30s ping keepalive
  useEffect(() => {
      if (!authenticated) return;
      const interval = setInterval(() => {
        API.ping().catch(() => {
          window.location.reload();
        });
      }, 30000);
      return () => clearInterval(interval);
    }, [authenticated]);
}
